import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaintBrush,
  faSave,
  faSpinner,
  faUpload,
  faImage,
  faTrash,
  faEye,
  faCode,
  faFont,
  faPalette,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import tenantService from '../services/tenantService';
import { useBranding } from '../contexts/BrandingContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';

const TenantSettings = () => {
  const { t } = useLanguage();
  const { refreshBranding } = useBranding();
  const { darkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const [brandingForm, setBrandingForm] = useState({
    appName: '',
    tagline: '',
    primaryColor: '#9333ea',
    secondaryColor: '#ef4444',
    footerText: '',
    customCss: ''
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [logoLightPreview, setLogoLightPreview] = useState(null);

  const logoInputRef = useRef(null);
  const logoLightInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getTenantSettings();
      setSettings(data);
      if (data?.branding) {
        setBrandingForm({
          appName: data.branding.appName || '',
          tagline: data.branding.tagline || '',
          primaryColor: data.branding.primaryColor || '#9333ea',
          secondaryColor: data.branding.secondaryColor || '#ef4444',
          footerText: data.branding.footerText || '',
          customCss: data.branding.customCss || ''
        });
        setLogoPreview(data.branding.logo || null);
        setLogoLightPreview(data.branding.logoLight || null);
      }
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      toast.error('Failed to load tenant settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSaving(true);
      await tenantService.updateBranding(brandingForm);
      toast.success('Branding settings saved successfully');
      if (refreshBranding) {
        refreshBranding();
      }
      fetchSettings();
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error(error.response?.data?.message || 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type = 'logo') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, SVG, and WebP are allowed.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;

        // Set local preview immediately
        if (type === 'logo') {
          setLogoPreview(base64Data);
        } else {
          setLogoLightPreview(base64Data);
        }

        try {
          await tenantService.uploadLogo({ [type]: base64Data });
          toast.success(`${type === 'logo' ? 'Logo' : 'Light logo'} uploaded successfully`);
          if (refreshBranding) {
            refreshBranding();
          }
        } catch (uploadError) {
          console.error('Error uploading logo:', uploadError);
          toast.error(uploadError.response?.data?.message || 'Failed to upload logo');
          // Revert preview on failure
          if (type === 'logo') {
            setLogoPreview(settings?.branding?.logo || null);
          } else {
            setLogoLightPreview(settings?.branding?.logoLight || null);
          }
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing logo:', error);
      toast.error('Failed to process image');
    }
  };

  const handleRemoveLogo = async (type = 'logo') => {
    if (!window.confirm(`Are you sure you want to remove the ${type === 'logo' ? 'dark' : 'light'} logo?`)) return;
    try {
      await tenantService.removeLogo(type);
      toast.success('Logo removed successfully');
      if (type === 'logo') {
        setLogoPreview(null);
      } else {
        setLogoLightPreview(null);
      }
      if (refreshBranding) {
        refreshBranding();
      }
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center h-64">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <FontAwesomeIcon icon={faPaintBrush} className="mr-3" />
            Tenant Branding Settings
          </h1>
          <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Customize the appearance of your tenant portal
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Settings Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Logo Upload Section */}
            <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faImage} className="mr-2 text-purple-500" />
                Logos
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Dark Logo */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Logo (Dark Background)
                  </label>
                  <div className={`w-full h-32 rounded-lg border-2 border-dashed flex items-center justify-center mb-3 ${
                    darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                  }`}>
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="max-w-full max-h-full object-contain p-2"
                      />
                    ) : (
                      <div className="text-center">
                        <FontAwesomeIcon icon={faImage} className="text-3xl text-gray-400 mb-1" />
                        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No logo uploaded</p>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="file"
                      ref={logoInputRef}
                      onChange={(e) => handleLogoUpload(e, 'logo')}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                      <FontAwesomeIcon icon={faUpload} className="mr-1" />
                      Upload
                    </button>
                    {logoPreview && (
                      <button
                        onClick={() => handleRemoveLogo('logo')}
                        className="px-3 py-1.5 text-red-500 border border-red-500 rounded hover:bg-red-500 hover:text-white text-sm"
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Light Logo */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Logo (Light Background)
                  </label>
                  <div className={`w-full h-32 rounded-lg border-2 border-dashed flex items-center justify-center mb-3 ${
                    darkMode ? 'border-gray-600 bg-gray-200' : 'border-gray-300 bg-white'
                  }`}>
                    {logoLightPreview ? (
                      <img
                        src={logoLightPreview}
                        alt="Light Logo"
                        className="max-w-full max-h-full object-contain p-2"
                      />
                    ) : (
                      <div className="text-center">
                        <FontAwesomeIcon icon={faImage} className="text-3xl text-gray-400 mb-1" />
                        <p className="text-xs text-gray-400">No logo uploaded</p>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <input
                      type="file"
                      ref={logoLightInputRef}
                      onChange={(e) => handleLogoUpload(e, 'logoLight')}
                      accept="image/*"
                      className="hidden"
                    />
                    <button
                      onClick={() => logoLightInputRef.current?.click()}
                      className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                      <FontAwesomeIcon icon={faUpload} className="mr-1" />
                      Upload
                    </button>
                    {logoLightPreview && (
                      <button
                        onClick={() => handleRemoveLogo('logoLight')}
                        className="px-3 py-1.5 text-red-500 border border-red-500 rounded hover:bg-red-500 hover:text-white text-sm"
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Recommended: PNG or SVG, max 5MB. Provide both for proper display on dark and light backgrounds.
              </p>
            </div>

            {/* App Name & Tagline */}
            <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faFont} className="mr-2 text-blue-500" />
                Text & Identity
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Application Name
                  </label>
                  <input
                    type="text"
                    value={brandingForm.appName}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, appName: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="My Security Platform"
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tagline
                  </label>
                  <input
                    type="text"
                    value={brandingForm.tagline}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, tagline: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Purple Team Operations Platform"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Footer Text
                </label>
                <input
                  type="text"
                  value={brandingForm.footerText}
                  onChange={(e) => setBrandingForm(prev => ({ ...prev, footerText: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border ${
                    darkMode
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Your Company. All rights reserved."
                />
              </div>
            </div>

            {/* Colors */}
            <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faPalette} className="mr-2 text-green-500" />
                Colors
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={brandingForm.primaryColor}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={brandingForm.primaryColor}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className={`flex-1 px-3 py-2 rounded border ${
                        darkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="#9333ea"
                    />
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Used for buttons, links, and accent elements
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Secondary Color
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={brandingForm.secondaryColor}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <input
                      type="text"
                      value={brandingForm.secondaryColor}
                      onChange={(e) => setBrandingForm(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className={`flex-1 px-3 py-2 rounded border ${
                        darkMode
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                      placeholder="#ef4444"
                    />
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Used for alerts, highlights, and secondary actions
                  </p>
                </div>
              </div>

              {/* Color Swatches Preview */}
              <div className="mt-4 flex space-x-3">
                <div
                  className="w-16 h-8 rounded shadow-inner border border-white/20"
                  style={{ backgroundColor: brandingForm.primaryColor }}
                  title="Primary"
                />
                <div
                  className="w-16 h-8 rounded shadow-inner border border-white/20"
                  style={{ backgroundColor: brandingForm.secondaryColor }}
                  title="Secondary"
                />
                <div
                  className="flex-1 h-8 rounded shadow-inner"
                  style={{
                    background: `linear-gradient(to right, ${brandingForm.primaryColor}, ${brandingForm.secondaryColor})`
                  }}
                  title="Gradient"
                />
              </div>
            </div>

            {/* Custom CSS */}
            <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow p-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faCode} className="mr-2 text-yellow-500" />
                Custom CSS
              </h2>
              <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Advanced: Add custom CSS to further customize the appearance. Use with caution.
              </p>
              <textarea
                value={brandingForm.customCss}
                onChange={(e) => setBrandingForm(prev => ({ ...prev, customCss: e.target.value }))}
                rows={8}
                className={`w-full px-3 py-2 rounded border font-mono text-sm ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-green-400 placeholder-gray-500'
                    : 'bg-gray-50 border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                placeholder={`/* Custom CSS for your tenant */\n.sidebar {\n  /* your styles here */\n}`}
              />
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSaveBranding}
                disabled={saving}
                className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 font-medium"
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Save Branding Settings
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Column: Live Preview */}
          <div className="lg:col-span-1">
            <div className={`rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow p-6 sticky top-6`}>
              <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faEye} className="mr-2 text-cyan-500" />
                Login Page Preview
              </h2>

              {/* Mini Login Preview */}
              <div className="rounded-lg overflow-hidden border border-gray-700">
                <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
                  {/* Logo */}
                  <div className="flex justify-center mb-4">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="h-12 w-auto max-w-[120px] object-contain"
                      />
                    ) : (
                      <div
                        className="p-3 rounded-full"
                        style={{
                          backgroundImage: `linear-gradient(to right, #dc2626, ${brandingForm.primaryColor})`
                        }}
                      >
                        <FontAwesomeIcon icon={faShieldAlt} className="text-white text-xl" />
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-center text-white text-sm font-bold mb-1">
                    {brandingForm.appName || 'Application Name'}
                  </h3>
                  <p className="text-center text-gray-400 text-xs mb-4">
                    {brandingForm.tagline || 'Your tagline here'}
                  </p>

                  {/* Fake Form */}
                  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="mb-3">
                      <div className="h-2 w-12 bg-gray-600 rounded mb-1.5" />
                      <div className="h-7 bg-gray-700 rounded border border-gray-600" />
                    </div>
                    <div className="mb-3">
                      <div className="h-2 w-16 bg-gray-600 rounded mb-1.5" />
                      <div className="h-7 bg-gray-700 rounded border border-gray-600" />
                    </div>
                    <button
                      className="w-full h-7 rounded text-white text-xs font-medium"
                      style={{ backgroundColor: brandingForm.primaryColor }}
                      disabled
                    >
                      Sign In
                    </button>
                  </div>

                  {/* Team Indicators */}
                  <div className="flex justify-center space-x-4 mt-4">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-1" />
                      <span className="text-red-400 text-xs">Red</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-1" />
                      <span className="text-blue-400 text-xs">Blue</span>
                    </div>
                    <div className="flex items-center">
                      <div
                        className="w-2 h-2 rounded-full mr-1"
                        style={{ backgroundColor: brandingForm.primaryColor }}
                      />
                      <span className="text-xs" style={{ color: brandingForm.primaryColor }}>
                        Purple
                      </span>
                    </div>
                  </div>

                  {/* Footer */}
                  <p className="text-center text-gray-500 text-xs mt-4">
                    {brandingForm.footerText || `${brandingForm.appName || 'App'}. Powered by Zerberos`}
                  </p>
                </div>
              </div>

              {/* Button Preview */}
              <div className="mt-6">
                <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Button Styles
                </h3>
                <div className="space-y-2">
                  <button
                    className="w-full px-4 py-2 rounded text-white text-sm font-medium"
                    style={{ backgroundColor: brandingForm.primaryColor }}
                    disabled
                  >
                    Primary Button
                  </button>
                  <button
                    className="w-full px-4 py-2 rounded text-white text-sm font-medium"
                    style={{ backgroundColor: brandingForm.secondaryColor }}
                    disabled
                  >
                    Secondary Button
                  </button>
                  <button
                    className="w-full px-4 py-2 rounded text-white text-sm font-medium"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${brandingForm.primaryColor}, ${brandingForm.secondaryColor})`
                    }}
                    disabled
                  >
                    Gradient Button
                  </button>
                </div>
              </div>

              {/* Current Settings Info */}
              {settings && (
                <div className={`mt-6 p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h3 className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tenant Info
                  </h3>
                  <div className={`text-xs space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {settings.name && <p>Name: {settings.name}</p>}
                    {settings.subdomain && <p>Subdomain: {settings.subdomain}</p>}
                    {settings.plan && <p>Plan: {settings.plan}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;
